import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Grid } from "@mui/material";
import FileUpload from "../../../components/FileUpload";
import { UploadedFiles } from "../../../types/venue";
import { toast as toastify } from "react-toastify";

interface Props {
  uploadedFiles: UploadedFiles;
  handleFilesChange: (category: keyof UploadedFiles) => (files: File[]) => void;
  existingFiles?: {
    venue: string[];
    paSystem: string[];
    equipment: string[];
    floorPlan: string[];
  };
  onExistingFilesChange?: (
    category: keyof UploadedFiles,
    remainingImages: string[]
  ) => void;
}

const AddVenuePhotos: React.FC<Props> = ({
  uploadedFiles,
  handleFilesChange,
  existingFiles = { venue: [], paSystem: [], equipment: [], floorPlan: [] },
  onExistingFilesChange,
}) => {
  const [tab, setTab] = useState(0);
  const [remainingImages, setRemainingImages] = useState(existingFiles);

  const categories: { key: keyof UploadedFiles; label: string }[] = [
    { key: "venue", label: "Venue" },
    { key: "floorPlan", label: "Floor Plans" },
    { key: "equipment", label: "Equipment" },
    { key: "paSystem", label: "P.A System" },
  ];

  const currentCategory = categories[tab].key;

  useEffect(() => {
    setRemainingImages(existingFiles);
  }, [existingFiles]);

  const handleExistingDelete = (
    category: keyof UploadedFiles,
    index: number
  ) => {
    const updated = remainingImages[category].filter((_, i) => i !== index);
    setRemainingImages((prev) => ({ ...prev, [category]: updated }));
    onExistingFilesChange?.(category, updated);
  };

  const buildFilesProp = (category: keyof UploadedFiles) => {
    const existingItems = (remainingImages[category] || []).map((url, idx) => ({
      preview: url,
      name: `existing_${idx}`,
      isExisting: true,
      complete: true,
      sizeKB: 0,
      uploadedKB: 0,
    }));

    const uploadedItems = (uploadedFiles[category] || []).map((file) =>
      Object.assign(file, {
        preview: (file as any).preview || URL.createObjectURL(file),
        complete: true,
        sizeKB: file.size / 1024,
        uploadedKB: file.size / 1024,
      })
    );

    return [...existingItems, ...uploadedItems];
  };

  const handleUploadAppend =
    (category: keyof UploadedFiles) => (newFiles: File[]) => {
      const currentFiles = uploadedFiles[category] || [];

      const filesWithPreviews = newFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          complete: true,
          sizeKB: file.size / 1024,
          uploadedKB: file.size / 1024,
        })
      );

      handleFilesChange(category)([...currentFiles, ...filesWithPreviews]);
    };

  const handleDelete = (category: keyof UploadedFiles) => (name: string) => {
    if (name.startsWith("existing_")) {
      const idx = parseInt(name.split("_")[1], 10);
      handleExistingDelete(category, idx);
      return;
    }

    const remaining = (uploadedFiles[category] || []).filter(
      (f) => f.name !== name
    );
    handleFilesChange(category)(remaining);
  };

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, val) => setTab(val)}
        sx={{ mb: 2 }}
        textColor="primary"
        indicatorColor="primary"
      >
        {categories.map((c, idx) => (
          <Tab
            key={c.key}
            label={c.label}
            value={idx}
            sx={{ textTransform: "none" }}
          />
        ))}
      </Tabs>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <FileUpload
            label={`Upload ${categories[tab].label} Photos`}
            files={buildFilesProp(currentCategory)}
            onChange={handleUploadAppend(currentCategory)}
            onDelete={handleDelete(currentCategory)}
            acceptedFileTypes={
              currentCategory === "floorPlan"
                ? "image/png,image/jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                : "image/png,image/jpeg"
            }
            hint={
              currentCategory === "floorPlan"
                ? "Guide:PDF and DOCX up to 2MB. Recommended size 1920x1080,ratios 16:9 or 4:3."
                : "Guide :JPEG or PNG, max 2MB.Recommended size 1920x1080,ratios 16:9 or 4:3."
            }
            toast={{ error: (msg: string) => toastify.error(msg) }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddVenuePhotos;
